import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {RoomPlannerProductWithTemplate} from 'components/customer/RoomPlanner/types';

const appliedPanelWithWidth = [
    'ManualPanelInterior.xml',
    'ManualPanelExterior.xml',
];

const appliedPanelWithLength = ['PanelAdjustableHoles.xml'];

/**
 * Checks if a template belongs to the Floating Vanity family.
 * @param {string} [templateName] - Template file name
 * @return {boolean} True if the template is a Floating Vanity
 */
export const isFloatingVanityTemplate = (templateName?: string): boolean =>
    Boolean(templateName?.includes('FloatingVanity'));

/**
 * Checks if a template is an applied panel with depth
 * @param {string} templateName - Template name
 * @return {boolean} True if applied panel with depth
 */
export const isAppliedPanelWithDepth = (templateName: string) => {
    return (
        (templateName?.includes('AppliedPanel') ||
            templateName?.includes('ManualPanel')) &&
        !appliedPanelWithWidth.includes(templateName)
    );
};

/**
 * Checks if a template is an applied panel with width
 * @param {string} templateName - Template name
 * @return {boolean} True if applied panel with width
 */
export const isAppliedPanelWithWidth = (templateName: string) => {
    return appliedPanelWithWidth.includes(templateName);
};

/**
 * Checks if a template is an applied panel with length
 * @param {string} templateName - Template name
 * @return {boolean} True if applied panel with length
 */
export const isAppliedPanelWithLength = (templateName: string) => {
    return appliedPanelWithLength.includes(templateName);
};

/**
 * Checks if a product is a manual panel (not a non-manual panel)
 * @param {string} templateName - Template name
 * @param {TemplateVariable} [variables] - Template variables
 * @return {boolean} True if manual panel
 */
export const isManualPanel = (
    templateName: string,
    variables?: TemplateVariable
) => {
    return (
        appliedPanelWithWidth.includes(templateName) &&
        !variables?.isNonManualPanel
    );
};

/**
 * Products that do not need adjustable legs: Panel, filler, vermin, kickboard,
 * bulkhead, inner-drawer-only (Drawer), Door (non-manual panel), key shape,
 * L-shaped panel, U-shaped panel, Floating Vanity.
 * @param {string} templateName - Template file name (e.g. from template_3d attributes)
 * @param {TemplateVariable} [variables] - Parsed template variables
 * @return {boolean} True if this product does not need adjustable legs
 * @param {RoomPlannerProductWithTemplate} values - Product values from room planner state
 */
export const productThatDoesNotNeedAdjustableLegs = (
    templateName: string,
    variables: TemplateVariable,
    values?: RoomPlannerProductWithTemplate
): boolean => {
    const isPanelProduct =
        isAppliedPanelWithDepth(templateName) ||
        isAppliedPanelWithWidth(templateName) ||
        isAppliedPanelWithLength(templateName) ||
        Boolean(variables?.isPanelProduct);
    const isVerminProduct = Boolean(values?.is_vermin);
    const isFillerProduct =
        templateName?.includes('Filler') && !isVerminProduct;
    const isFloorProduct = templateName?.includes('KickerFramed');
    const vars = variables as
        (TemplateVariable & {isBulkheadComponent?: boolean}) | undefined;
    const isBulkhead =
        Boolean(vars?.isUpperProduct) ||
        Boolean(vars?.isBulkheadComponent) ||
        templateName?.includes('Bulkhead');
    const isInnerDrawerOnly = Boolean(variables?.innerDrawerOnly);
    const isNonManualPanel = Boolean(variables?.isNonManualPanel);
    const isKeyShapePanel =
        Boolean(variables?.isKeyShapePanel) ||
        templateName?.includes('KeyShapePanel');
    const isLShapePanel =
        Boolean(variables?.isLShapePanel) ||
        templateName?.includes('LShapePanel');
    const isUShapedPanel = Boolean(variables?.isUShapedPanel);
    const isFloatingVanity = isFloatingVanityTemplate(templateName);

    return (
        isPanelProduct ||
        isFillerProduct ||
        isVerminProduct ||
        isFloorProduct ||
        isBulkhead ||
        isInnerDrawerOnly ||
        isNonManualPanel ||
        isKeyShapePanel ||
        isLShapePanel ||
        isUShapedPanel ||
        isFloatingVanity
    );
};
